import React from 'react';
import { motion } from 'framer-motion';
import { Plug, Calculator, Database } from 'lucide-react';
import { AnimationBoardTarget } from './AnimationBoardTarget';
import { useTranslation } from 'react-i18next';

export const SpecsAnimation: React.FC = () => {
    const { t } = useTranslation('common');

    return (
        <div className="relative h-56 sm:h-72 w-full flex items-center bg-neutral-50 rounded-xl border border-neutral-200 p-4 sm:p-6 overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-accent-primary/5 rounded-full blur-[80px] pointer-events-none" />

            {/* Connection Block (Left) */}
            <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0 }}
                className="flex flex-col items-center gap-1 sm:gap-2 relative z-20 flex-shrink-0"
            >
                <div className="w-14 sm:w-20 h-24 sm:h-32 bg-white rounded-xl sm:rounded-2xl border-2 border-accent-primary flex flex-col items-center justify-center relative overflow-hidden shadow-sm">
                    <div className="absolute inset-0 bg-accent-primary/[0.03]" />
                    <Plug className="w-5 sm:w-7 h-5 sm:h-7 text-accent-primary mb-2 sm:mb-3 relative z-10" />

                    <div className="flex flex-wrap justify-center gap-1 sm:gap-1.5 px-2 sm:px-4 relative z-10">
                        {[...Array(4)].map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.1 * i }}
                                className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-accent-primary/20"
                            />
                        ))}
                    </div>

                    {/* Output Ports (Moved closer to center: 35% from top/bottom) */}
                    <div className="absolute right-0 top-[35%] w-1 sm:w-1.5 h-0.5 sm:h-1 bg-blue-500 rounded-l z-20" />
                    <div className="absolute right-0 bottom-[35%] w-1 sm:w-1.5 h-0.5 sm:h-1 bg-emerald-500 rounded-l z-20 shadow-[0_0_4px_#3b82f6]" />
                </div>
                <span className="text-[8px] sm:text-[10px] text-accent-primary font-bold uppercase tracking-widest bg-accent-primary/5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-accent-primary/10 whitespace-nowrap">
                    {t('featureExplanation.connection.label', 'Connection')}
                </span>
            </motion.div>

            {/* Lines Container - Gap reduced to match 35% port positions (0.3 * height) */}
            <div className="flex-1 flex flex-col justify-center gap-[28.8px] sm:gap-[38.4px] ml-2 sm:ml-4 mr-2 sm:mr-4 relative min-w-0">
                {/* Direct Line (Top) */}
                <div className="relative h-px">
                    <div className="absolute left-0 top-0 w-full h-px bg-neutral-200" />
                    <motion.div
                        animate={{ left: ['0%', '100%'], opacity: [0, 1, 0] }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0, ease: "linear" }}
                        className="absolute top-1/2 -translate-y-1/2 text-blue-500 select-none pointer-events-none"
                    >
                        <Database className="w-3 sm:w-4 h-3 sm:h-4 fill-current" />
                    </motion.div>
                </div>

                {/* Line with Formula Block (Bottom) */}
                <div className="relative flex items-center">
                    {/* First segment */}
                    <div className="flex-1 relative h-px min-w-[15px] sm:min-w-[20px]">
                        <div className="absolute left-0 top-0 w-full h-px bg-neutral-200" />
                        <motion.div
                            animate={{
                                left: ['0%', '100%'],
                                opacity: [0, 1, 1]
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: 0.5,
                                ease: "linear",
                                times: [0, 0.3, 1]
                            }}
                            className="absolute top-1/2 -translate-y-1/2 text-emerald-500 select-none pointer-events-none"
                        >
                            <Database className="w-3 sm:w-4 h-3 sm:h-4 fill-current" />
                        </motion.div>
                    </div>

                    {/* Spec Block (Middle) - Proportional height Scaling */}
                    <div className="flex flex-col items-center gap-1 sm:gap-2 mx-1 sm:mx-2 flex-shrink-0">
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.6, type: "spring" }}
                            className="w-10 sm:w-14 h-12 sm:h-16 bg-white rounded-lg sm:rounded-xl border-2 border-purple-500/20 flex items-center justify-center shadow-sm z-10 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-purple-500/[0.03]" />
                            <Calculator className="w-4 sm:w-6 h-4 sm:h-6 text-purple-500 relative z-10" />
                        </motion.div>
                        <span className="text-[8px] sm:text-[10px] text-accent-primary font-bold uppercase tracking-widest bg-accent-primary/5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-accent-primary/10 whitespace-nowrap">
                            {t('featureExplanation.spec.label', 'Spec')}
                        </span>
                    </div>

                    {/* Second segment */}
                    <div className="flex-1 relative h-px min-w-[15px] sm:min-w-[20px]">
                        <div className="absolute left-0 top-0 w-full h-px bg-neutral-200" />
                        <motion.div
                            animate={{
                                left: ['-10%', '100%'],
                                opacity: [1, 1, 0]
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: 1.5,
                                ease: "linear",
                                times: [0, 0.7, 1]
                            }}
                            className="absolute top-1/2 -translate-y-1/2 text-emerald-500 select-none pointer-events-none"
                        >
                            <Database className="w-3 sm:w-4 h-3 sm:h-4 fill-current" />
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="relative z-20 flex-shrink-0">
                <AnimationBoardTarget type="specs" />
            </div>
        </div>
    );
};